import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { Cell, CellWithHierarchy } from '@pelckmans/elementis/components/table';
import { ComponentProps, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HorizontalScrollContext from '../../../../../shared-components/table/context/HorizontalScrollContext';
import { TableDataGroup } from '../types';
import { TableRow } from './TableRow';

const GroupHeader = ({
  row,
  setCollapsed,
  isCollapsed,
  detailView,
}: {
  row: TableDataGroup;
  setCollapsed: (collapsed: boolean | ((c: boolean) => boolean)) => void;
  isCollapsed: boolean;
  detailView: boolean;
}) => {
  const { horizontalScroll } = useContext(HorizontalScrollContext);
  const { t } = useTranslation();

  return (
    <tr>
      <CellWithHierarchy
        hierarchyProps={{
          level: row.accumulatedRow.level as ComponentProps<typeof CellWithHierarchy>['hierarchyProps']['level'],
          onToggleCollapsed: () => setCollapsed(c => !c),
          collapseButtonLabelText: t('PAGES.RESULTS.PER_STUDENT.TABLE.TOC_NODE.COLLAPSE_BUTTON.A11Y_LABEL'),
          collapsed: isCollapsed,
        }}
        highlighted
        density="normal"
        sticky={!detailView && horizontalScroll}
        minWidth="xl"
        maxWidth="xl"
      >
        <Cell.Text
          title={row.accumulatedRow.title}
          prominent
          subTitle={
            row.accumulatedRow.subTitleTop ? { text: row.accumulatedRow.subTitleTop, position: 'top' } : undefined
          }
          startAdornment={
            row.accumulatedRow.image ? (
              <Avatar>
                <AvatarImage
                  size="sm"
                  name={t('PAGES.RESULTS.PER_STUDENT.TABLE.TOC_NODE.AVATAR.A11Y_LABEL')}
                  src={row.accumulatedRow.image}
                />
              </Avatar>
            ) : undefined
          }
        />
      </CellWithHierarchy>
      {row.accumulatedRow.columns.map((column, i) => {
        if (column && 'Cell' in column) {
          const CustomCell = column.Cell;
          return <CustomCell key={column.id} />;
        }

        return column && 'score' in column && column.score ? (
          <Cell key={column.id} highlighted minWidth="xs" maxWidth="xs">
            <Cell.Results
              awaiting={column.pending}
              success={column.score.achieved}
              failure={column.score.achievable - column.score.achieved}
              undone={column.score.maximum - (column.score.achievable + column.pending)}
              quantifiable
              variant="donut"
              showTooltip
            />
          </Cell>
        ) : (
          <Cell key={i} highlighted />
        );
      })}
    </tr>
  );
};

const TableGroup = ({ row, detailView }: { row: TableDataGroup; detailView: boolean }) => {
  const [isCollapsed, setCollapsed] = useState(false);
  return (
    <>
      <tbody data-testid={`table-body-${row.id}`}>
        <GroupHeader isCollapsed={isCollapsed} setCollapsed={setCollapsed} row={row} detailView={detailView} />
        {!isCollapsed &&
          row.rows.map(row => <TableRow key={`table-row-${row.id}`} row={row} detailView={detailView} />)}
      </tbody>
      {!isCollapsed &&
        row.groups &&
        row.groups.map(group => <TableGroup key={`table-group-${group.id}`} row={group} detailView={detailView} />)}
    </>
  );
};

export default TableGroup;
